import { useSiteHome } from '@/lib/queries';
import type { Uuid } from '@/lib/types';
import { usePhotoUrl } from '@/lib/usePhotoUrl';

/**
 * 홈 머리 — <b>「여기가 어디이고 지금 어디까지 왔나」</b>. 등록부의 `site` 위젯이다(고정 · wide).
 *
 * <p>포털을 열면 가장 먼저 묻는 것이 그것이다. 현장 이름 한 줄로는 답이 되지 않고, 여러 현장에
 * 들어가 있는 협력사는 매번 이름을 읽어야 한다.
 *
 * <p>서버가 개요·공정·사진을 <b>한 번에</b> 준다({@code GET /sites/{id}/home}). 따로 부르면 이
 * 카드가 세 번에 걸쳐 조각조각 채워지고, 그 사이 사용자는 자기가 무엇을 보는지 모른다.
 *
 * <p>메이슨리 안의 한 칸이라 바깥 여백을 갖지 않는다 — front 에는 메이슨리 밖의 카드가 없다.
 */

export function SiteHeroCard({ siteId }: { siteId: Uuid }) {
  const query = useSiteHome(siteId);
  const site = query.data;
  /*
    전경사진은 서버를 거쳐 받는다. 서버는 키를 그대로 열지 않고 그 현장의 사진 키와 견준다 —
    다른 현장의 키를 넣으면 403 이 나고 감사에 남는다(ExternalSiteInfoController.photo).
  */
  const photoKey = site?.photoKey ?? null;
  const photo = usePhotoUrl(photoKey === null ? null : `/sites/${siteId}/photo?key=${encodeURIComponent(photoKey)}`);

  if (query.isPending) {
    return (
      <div className="card" aria-hidden>
        <div style={{ width: '45%', height: 20, borderRadius: 6, background: 'var(--bg-hover)' }} />
      </div>
    );
  }

  // 못 불러왔으면 머리를 비운다 — 「불러오지 못했습니다」를 크게 띄우면 홈이 고장 난 것처럼 보인다.
  if (query.isError || site === undefined) {
    return null;
  }

  const behind = site.plannedRate !== null && site.progressRate !== null
    && site.progressRate < site.plannedRate;

  return (
    <section className="card overflow-hidden p-0">
      {/*
        전경사진이 있을 때만 그림을 깐다. 없는 것이 정상이라(착공 전·안 올린 현장)
        「사진 없음」 딱지를 세우지 않는다 — 그것은 사용자 자료를 탓하는 말이다.
        바이트가 도착하기 전에는 자리도 만들지 않는다 — 깨진 이미지 아이콘이 잠깐이라도 보이면 고장으로 읽힌다.
      */}
      {photo !== null && (
        <div className="relative h-32 w-full sm:h-40">
          <img src={photo} alt={site.photoCaption ?? site.name} className="h-full w-full object-cover" />
        </div>
      )}

      <div className="p-4">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="truncate text-ui-xl font-semibold">{site.name}</h1>
            <div className="mt-0.5 truncate text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
              {[site.siteCategory, site.facilityType, site.scale].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {site.readOnly && <span className="badge badge-gray">조회만</span>}
            {site.status !== null && <span className="badge badge-blue">{site.status}</span>}
          </div>
        </div>

        {/*
          숫자 셋을 한 줄에. 「무엇이 밀렸나」가 공정율보다 먼저 읽혀야 해서 지연을 가운데 둔다.
          모바일에서도 세 칸이 들어간다 — 값이 짧아서다.
        */}
        <div className="mb-3 grid grid-cols-3 gap-2">
          <Stat
            label="공정율"
            value={site.progressRate === null ? '-' : `${site.progressRate}%`}
            sub={site.plannedRate === null ? undefined : `계획 ${site.plannedRate}%`}
            danger={behind}
          />
          <Stat
            label="지연 공정"
            value={`${site.delayedCount}건`}
            danger={site.delayedCount > 0}
          />
          <Stat
            label="준공까지"
            value={site.dDay === null ? '-' : site.dDay >= 0 ? `D-${site.dDay}` : `D+${-site.dDay}`}
            danger={site.dDay !== null && site.dDay < 0}
          />
        </div>

        <dl className="flex flex-col gap-1 border-t pt-3 text-ui-xs" style={{ borderColor: 'var(--border)' }}>
          {site.clientName !== null && <Row label="발주처" value={site.clientName} />}
          {site.contractStartDate !== null && (
            <Row label="공사기간" value={`${site.contractStartDate} ~ ${site.contractEndDate ?? ''}`} />
          )}
          {site.siteAddress !== null && <Row label="위치" value={site.siteAddress} />}
        </dl>
      </div>
    </section>
  );
}

function Stat({
  label,
  value,
  sub,
  danger = false,
}: {
  label: string;
  value: string;
  sub?: string;
  danger?: boolean;
}) {
  return (
    <div className="rounded-md p-2" style={{ background: 'var(--bg-hover)' }}>
      <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div
        className="text-ui-lg font-semibold"
        style={danger ? { color: 'var(--danger)' } : undefined}
      >
        {value}
      </div>
      {sub !== undefined && (
        <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>{sub}</div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt style={{ color: 'var(--text-secondary)' }}>{label}</dt>
      <dd className="min-w-0 truncate text-right">{value}</dd>
    </div>
  );
}
